import useSWR from "swr";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Panel, PanelHeader } from "@/components/common/Card";
import { Pill, GradePill, DirectionPill, StatusPill, ThesisInvalidationBadge } from "@/components/common/Pill";
import {
  getSignal,
  generateAiReview,
  isClosed,
  signalRMultiple,
  formatNum,
  formatPct,
  formatSignedPct,
  formatR,
  timeAgo,
} from "@/lib/api";
import { SIGNALS, AIREVIEW } from "@/constants/testIds";
import {
  ArrowLeft,
  Target,
  Lightning,
  Activity,
  CheckCircle,
  XCircle,
  Circle,
  Flag,
  Cpu,
  ChartLineUp,
  Stack,
  ShieldCheck,
  TrendUp,
  TrendDown,
  Sparkle,
  Warning,
} from "@phosphor-icons/react";

const STRAT_LABEL = {
  trend_pullback: "Trend Pullback",
  breakout_retest: "Breakout Retest",
  range_trading: "Range Trading",
  liquidity_sweep: "Liquidity Sweep",
};

const TF_TONE = {
  bullish: "long",
  bearish: "short",
  confirmed: "cyan",
  neutral: "neutral",
  ranging: "yellow",
};

const Stat = ({ label, value, accent = "text-slate-900", testid }) => (
  <div className="px-4 py-3" data-testid={testid}>
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
      {label}
    </div>
    <div className={`mt-1 text-lg font-data ${accent}`}>{value}</div>
  </div>
);

const ScoreBar = ({ label, value, max = 100 }) => {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const tone =
    pct >= 75
      ? "bg-[#16a34a]"
      : pct >= 50
        ? "bg-[#2563eb]"
        : pct >= 25
          ? "bg-[#d97706]"
          : "bg-[#dc2626]";
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-slate-700 tracking-wide capitalize">
          {label.replace(/_/g, " ")}
        </span>
        <span className="text-slate-600 font-data">
          {formatNum(value, 1)}
          <span className="text-slate-400"> / {max}</span>
        </span>
      </div>
      <div className="h-1.5 bg-[#e2e8f0] rounded-sm overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const TpProgress = ({ pct }) => (
  <div className="h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-[#2563eb] to-[#16a34a] transition-all"
      style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
    />
  </div>
);

const StepNode = ({ step, isLast }) => {
  const toneRing = {
    cyan: "border-[#2563eb] text-[#2563eb]",
    green: "border-[#16a34a] text-[#16a34a]",
    red: "border-[#dc2626] text-[#dc2626]",
    neutral: "border-slate-400 text-slate-600",
  };
  const done = step.done;
  return (
    <div className="flex gap-3" data-testid={`lifecycle-step-${step.label}`}>
      <div className="flex flex-col items-center">
        <div
          className={`h-7 w-7 rounded-full border-2 flex items-center justify-center shrink-0 ${
            done
              ? toneRing[step.tone] + " bg-[#ffffff]"
              : "border-slate-200 text-slate-400 bg-[#f1f5f9]"
          }`}
        >
          {done ? (
            step.tone === "red" ? (
              <XCircle size={14} weight="fill" />
            ) : (
              <CheckCircle size={14} weight="fill" />
            )
          ) : (
            <Circle size={10} weight="bold" />
          )}
        </div>
        {!isLast && (
          <div
            className={`w-0.5 flex-1 min-h-[28px] ${
              done ? "bg-slate-900/15" : "bg-slate-900/5"
            }`}
          />
        )}
      </div>
      <div className="pb-5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-900">{step.label}</span>
          {step.pct !== undefined && step.pct !== null && (
            <span
              className={`text-[11px] font-data ${
                step.pct >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"
              }`}
            >
              {formatSignedPct(step.pct)}
            </span>
          )}
        </div>
        <div className="text-xs font-data text-slate-600 mt-0.5">{step.value}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          {step.at ? timeAgo(step.at) : done ? "—" : "Pending"}
        </div>
      </div>
    </div>
  );
};

const LifecyclePanel = ({ s }) => {
  const entry = Number(s.entry);
  const dir = s.direction;
  const legPct = (price) => {
    if (price === null || price === undefined || !entry) return null;
    const p = Number(price);
    return dir === "LONG" ? ((p - entry) / entry) * 100 : ((entry - p) / entry) * 100;
  };

  const tps = [
    { key: "TP1", price: s.tp1, at: s.tp1_hit_at, weight: 33 },
    { key: "TP2", price: s.tp2, at: s.tp2_hit_at, weight: 33 },
    { key: "TP3", price: s.tp3, at: s.tp3_hit_at, weight: 34 },
  ];
  const hitCount = tps.filter((t) => t.at).length;
  const slHit = !!s.sl_hit_at;
  const progressPct = (hitCount / 3) * 100;

  const steps = [
    {
      label: "Entry",
      value: formatNum(entry),
      at: s.created_at,
      done: true,
      tone: "cyan",
    },
    ...tps.map((t) => ({
      label: t.key,
      value: formatNum(t.price),
      at: t.at,
      done: !!t.at,
      tone: "green",
      pct: legPct(t.price),
    })),
  ];
  const exitStep = slHit
    ? {
        label: "Stop Loss",
        value: formatNum(s.stop_loss),
        at: s.sl_hit_at,
        done: true,
        tone: "red",
        pct: legPct(s.stop_loss),
      }
    : s.closed_at
      ? {
          label: "Closed",
          value: formatNum(s.exit_price ?? s.entry),
          at: s.closed_at,
          done: true,
          tone: "neutral",
        }
      : { label: "Exit", value: "—", at: null, done: false, tone: "neutral" };
  const allSteps = [...steps, exitStep];

  const blended = s.pnl_percent;
  const rMultiple = signalRMultiple(s);

  return (
    <Panel className="lg:col-span-12" data-testid="signal-lifecycle">
      <PanelHeader
        title="Trade Lifecycle"
        hint="Multi-TP progression, excursions & blended PnL"
        action={<Flag size={14} className="text-slate-500" />}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-300/5">
        {/* Vertical stepper */}
        <div className="lg:col-span-4 p-4" data-testid="lifecycle-stepper">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">
            Progression
          </div>
          {allSteps.map((st, i) => (
            <StepNode
              key={st.label}
              step={st}
              isLast={i === allSteps.length - 1}
            />
          ))}
        </div>

        {/* Progress + excursions + per-leg */}
        <div className="lg:col-span-8 p-4 flex flex-col gap-5">
          {/* Horizontal progress */}
          <div data-testid="lifecycle-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                TP Progress
              </span>
              <span className="text-xs font-data text-slate-700">
                {hitCount} / 3 targets
              </span>
            </div>
            <TpProgress pct={progressPct} />
            <div className="flex justify-between mt-1.5">
              {["TP1", "TP2", "TP3"].map((t, i) => (
                <span
                  key={t}
                  className={`text-[10px] font-data ${
                    tps[i].at ? "text-[#16a34a]" : "text-slate-400"
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Excursions + blended PnL */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div
              className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2.5"
              data-testid="lifecycle-mfe"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 flex items-center gap-1">
                <TrendUp size={11} className="text-[#16a34a]" /> MFE
              </div>
              <div className="mt-1 text-lg font-data text-[#16a34a]">
                {s.max_favorable_excursion === null ||
                s.max_favorable_excursion === undefined
                  ? "—"
                  : formatPct(s.max_favorable_excursion)}
              </div>
            </div>
            <div
              className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2.5"
              data-testid="lifecycle-mae"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 flex items-center gap-1">
                <TrendDown size={11} className="text-[#dc2626]" /> MAE
              </div>
              <div className="mt-1 text-lg font-data text-[#dc2626]">
                {s.max_adverse_excursion === null ||
                s.max_adverse_excursion === undefined
                  ? "—"
                  : formatPct(s.max_adverse_excursion)}
              </div>
            </div>
            <div
              className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2.5"
              data-testid="lifecycle-pnl"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Blended PnL
              </div>
              <div
                className={`mt-1 text-lg font-data ${
                  (blended ?? 0) >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"
                }`}
              >
                {formatSignedPct(blended)}
              </div>
            </div>
            <div
              className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2.5"
              data-testid="lifecycle-r"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                R-Multiple
              </div>
              <div
                className={`mt-1 text-lg font-data ${
                  (rMultiple ?? 0) >= 0 ? "text-[#2563eb]" : "text-[#dc2626]"
                }`}
              >
                {formatR(rMultiple)}
              </div>
            </div>
          </div>

          {/* Per-leg breakdown */}
          <div data-testid="lifecycle-legs">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">
              Per-Leg Breakdown
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
                    <th className="text-left font-bold px-2 py-1.5">Leg</th>
                    <th className="text-right font-bold px-2 py-1.5">Target</th>
                    <th className="text-right font-bold px-2 py-1.5">Move %</th>
                    <th className="text-right font-bold px-2 py-1.5">Weight</th>
                    <th className="text-left font-bold px-2 py-1.5 pl-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tps.map((t) => (
                    <tr key={t.key} className="border-b border-slate-200/70">
                      <td className="px-2 py-2 font-data text-slate-900">{t.key}</td>
                      <td className="px-2 py-2 text-right font-data text-slate-700">
                        {formatNum(t.price)}
                      </td>
                      <td className="px-2 py-2 text-right font-data text-[#16a34a]">
                        {formatSignedPct(legPct(t.price))}
                      </td>
                      <td className="px-2 py-2 text-right font-data text-slate-600">
                        {t.weight}%
                      </td>
                      <td className="px-2 py-2 pl-4">
                        {t.at ? (
                          <Pill tone="success">Hit · {timeAgo(t.at)}</Pill>
                        ) : (
                          <Pill tone="neutral">Pending</Pill>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-slate-200/70">
                    <td className="px-2 py-2 font-data text-slate-900">SL</td>
                    <td className="px-2 py-2 text-right font-data text-slate-700">
                      {formatNum(s.stop_loss)}
                    </td>
                    <td className="px-2 py-2 text-right font-data text-[#dc2626]">
                      {formatSignedPct(legPct(s.stop_loss))}
                    </td>
                    <td className="px-2 py-2 text-right font-data text-slate-600">
                      —
                    </td>
                    <td className="px-2 py-2 pl-4">
                      {slHit ? (
                        <Pill tone="error">Hit · {timeAgo(s.sl_hit_at)}</Pill>
                      ) : (
                        <Pill tone="neutral">Pending</Pill>
                      )}
                    </td>
                  </tr>
                  <tr className="bg-[#f1f5f9]">
                    <td
                      className="px-2 py-2 font-bold text-slate-900 uppercase text-[11px] tracking-wide"
                      colSpan={2}
                    >
                      Blended Result
                    </td>
                    <td
                      className={`px-2 py-2 text-right font-data ${
                        (blended ?? 0) >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"
                      }`}
                    >
                      {formatSignedPct(blended)}
                    </td>
                    <td className="px-2 py-2 text-right font-data text-slate-500">
                      100%
                    </td>
                    <td className="px-2 py-2 pl-4">
                      <StatusPill status={s.status} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};

const GRADE_TONE = { A: "success", B: "cyan", C: "yellow", D: "warn", F: "error" };

const AiReviewPanel = ({ s, onGenerated }) => {
  const review = s.ai_review;
  const [loading, setLoading] = useState(false);
  const closed = isClosed(s);

  const generate = async () => {
    setLoading(true);
    try {
      await generateAiReview(s.id);
      await onGenerated();
      toast.success("AI review ready", { description: "Post-mortem generated by Claude." });
    } catch (e) {
      toast.error("AI review failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel className="lg:col-span-12" data-testid={AIREVIEW.panel}>
      <PanelHeader
        title="AI Trade Review"
        hint="Claude Sonnet 4.6 post-mortem on the completed trade"
        action={
          <button
            data-testid={AIREVIEW.generateBtn}
            onClick={generate}
            disabled={loading || !closed}
            title={closed ? "" : "Available once the trade is closed"}
            className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed bg-[#2563eb] text-white hover:bg-[#2563eb]/90"
          >
            <Sparkle size={12} weight="fill" />
            {loading ? "Analyzing…" : review ? "Regenerate" : "Generate Review"}
          </button>
        }
      />
      <div className="p-4">
        {!closed ? (
          <div className="text-xs text-slate-400 text-center py-8">
            AI review unlocks after the trade closes (TP / SL / Expired).
          </div>
        ) : !review ? (
          <div className="text-xs text-slate-500 text-center py-8">
            <Sparkle size={22} className="mx-auto mb-2 text-[#2563eb]" />
            Generate an objective post-mortem of this trade's setup, execution and lesson.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <Pill tone={GRADE_TONE[review.grade] || "neutral"} data-testid="ai-review-grade">
                Setup {review.grade || "—"}
              </Pill>
              <p className="text-sm text-slate-800 flex-1" data-testid="ai-review-verdict">
                {review.verdict}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#16a34a] mb-2">
                  What Worked
                </div>
                <div className="flex flex-col gap-1.5">
                  {(review.what_worked || []).length === 0 ? (
                    <span className="text-xs text-slate-400">—</span>
                  ) : (
                    review.what_worked.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                        <CheckCircle size={12} weight="fill" className="text-[#16a34a] mt-0.5 shrink-0" />
                        {w}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#dc2626] mb-2">
                  What Failed
                </div>
                <div className="flex flex-col gap-1.5">
                  {(review.what_failed || []).length === 0 ? (
                    <span className="text-xs text-slate-400">No notable weaknesses.</span>
                  ) : (
                    review.what_failed.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                        <XCircle size={12} weight="fill" className="text-[#dc2626] mt-0.5 shrink-0" />
                        {w}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2.5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">
                  Key Factor
                </div>
                <p className="text-xs text-slate-700">{review.key_factor || "—"}</p>
              </div>
              <div className="bg-[#2563eb]/5 border border-[#2563eb]/20 rounded-sm px-3 py-2.5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#2563eb] mb-1">
                  Lesson
                </div>
                <p className="text-xs text-slate-700">{review.lesson || "—"}</p>
              </div>
            </div>
            <div className="text-[10px] text-slate-400 font-data">
              {review.model} · {timeAgo(review.generated_at)}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
};

export const SignalDetail = () => {
  const { id } = useParams();
  const { data: s, error, isLoading, mutate } = useSWR(
    id ? ["/signals", id] : null,
    () => getSignal(id),
  );

  if (isLoading) {
    return (
      <div className="text-slate-500 text-sm">Loading signal…</div>
    );
  }

  if (error || !s) {
    return (
      <div className="max-w-2xl">
        <Link
          to="/signals"
          data-testid={SIGNALS.detailBack}
          className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft size={14} weight="bold" /> Back to signals
        </Link>
        <Panel className="p-6 text-center text-slate-600">
          Signal not found.
        </Panel>
      </div>
    );
  }

  // Derive conditions passed/failed:
  //  - Passed = explicit reasons list from backend
  //  - Failed = score_breakdown components scoring below 50% of their max (100)
  const reasons = Array.isArray(s.reasons) ? s.reasons : [];
  const breakdown = s.score_breakdown || {};
  const weakComponents = Object.entries(breakdown)
    .filter(([, v]) => Number(v) < 50)
    .map(([k, v]) => ({ name: k, value: Number(v) }));
  const strongComponents = Object.entries(breakdown)
    .filter(([, v]) => Number(v) >= 75)
    .map(([k, v]) => ({ name: k, value: Number(v) }));

  const timeframes = s.timeframes || {};

  return (
    <div
      data-testid={SIGNALS.detail}
      className="flex flex-col gap-6 max-w-[1600px]"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to="/signals"
            data-testid={SIGNALS.detailBack}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-slate-500 hover:text-slate-900 mb-2"
          >
            <ArrowLeft size={12} weight="bold" /> All Signals
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900">
              {s.pair}
            </h1>
            <DirectionPill direction={s.direction} />
            <GradePill grade={s.grade} />
          </div>
          <div className="text-xs text-slate-500 font-data mt-2">
            {STRAT_LABEL[s.strategy] || s.strategy} ·{" "}
            <span className="text-slate-700">{s.market_regime}</span> ·{" "}
            {timeAgo(s.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={s.status} />
          <ThesisInvalidationBadge signal={s} />
          {s.telegram_sent ? (
            <Pill tone="info">Telegram · Sent</Pill>
          ) : (
            <Pill tone="warn">Telegram · Off</Pill>
          )}
        </div>
      </div>

      {/* Thesis invalidation banner (advisory only — see thesis_invalidation.py) */}
      {(s.thesis_invalidated || s.thesis_warning_reason) && (
        <div
          data-testid="signal-thesis-banner"
          className={`rounded-sm border px-4 py-3 text-sm flex items-start gap-2.5 ${
            s.thesis_invalidated
              ? "bg-[#d97706]/10 border-[#d97706]/30 text-[#92400e]"
              : "bg-[#d97706]/5 border-[#d97706]/15 text-[#92400e]/80"
          }`}
        >
          <Warning size={16} weight="fill" className="text-[#d97706] mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-xs uppercase tracking-wide">
              {s.thesis_invalidated ? "Thesis Invalidated (advisory only)" : "Watching — possible thesis break"}
            </div>
            <div className="mt-0.5 text-slate-700">
              {s.thesis_invalidated ? s.thesis_invalidated_reason : s.thesis_warning_reason}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {s.thesis_invalidated
                ? "The position was not closed automatically — review and decide manually."
                : "Seen on the most recent scan; needs one more confirming check before it's flagged."}
            </div>
          </div>
        </div>
      )}

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Panel className="p-4" data-testid="signal-kpi-score">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Score
          </div>
          <div className="mt-2 text-3xl font-data text-slate-900">
            {formatNum(s.score, 1)}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            Grade {s.grade}
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Entry
          </div>
          <div className="mt-2 text-2xl font-data text-slate-900">
            {formatNum(s.entry)}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            {STRAT_LABEL[s.strategy] || s.strategy}
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Stop Loss
          </div>
          <div className="mt-2 text-2xl font-data text-[#dc2626]">
            {formatNum(s.stop_loss)}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            {formatPct(s.sl_percent)} risk
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Risk / Reward
          </div>
          <div className="mt-2 text-2xl font-data text-[#2563eb]">
            {formatNum(s.rr, 2)}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            Target average
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Leverage
          </div>
          <div className="mt-2 text-2xl font-data text-[#d97706]">
            {s.suggested_leverage || "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            Suggested
          </div>
        </Panel>
        <Panel className="p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Regime
          </div>
          <div className="mt-2 text-lg font-data text-slate-900 tracking-wide">
            {s.market_regime}
          </div>
          <div className="mt-1 text-xs text-slate-500 font-data">
            Mode · {s.mode || "—"}
          </div>
        </Panel>
      </div>

      {/* Main bento grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Trade Lifecycle — Phase 2 */}
        <LifecyclePanel s={s} />

        {/* AI Trade Review — Phase 3 */}
        <AiReviewPanel s={s} onGenerated={mutate} />

        {/* Entry / SL / TPs */}
        <Panel className="lg:col-span-5" data-testid="signal-trade-plan">
          <PanelHeader
            title="Trade Plan"
            hint="Entry, stops & take-profits"
            action={<Target size={14} className="text-slate-500" />}
          />
          <div className="divide-y divide-slate-300/5">
            <div className="grid grid-cols-2 gap-px bg-slate-900/5">
              <Stat
                testid="trade-entry"
                label="Entry"
                value={formatNum(s.entry)}
              />
              <Stat
                testid="trade-sl"
                label="Stop Loss"
                value={formatNum(s.stop_loss)}
                accent="text-[#dc2626]"
              />
            </div>
            <div className="grid grid-cols-3 gap-px bg-slate-900/5">
              <Stat
                testid="trade-tp1"
                label="TP1"
                value={formatNum(s.tp1)}
                accent="text-[#16a34a]"
              />
              <Stat
                testid="trade-tp2"
                label="TP2"
                value={formatNum(s.tp2)}
                accent="text-[#16a34a]"
              />
              <Stat
                testid="trade-tp3"
                label="TP3"
                value={formatNum(s.tp3)}
                accent="text-[#16a34a]"
              />
            </div>
            <div className="grid grid-cols-3 gap-px bg-slate-900/5">
              <Stat
                label="R / R"
                value={formatNum(s.rr, 2)}
                accent="text-[#2563eb]"
              />
              <Stat label="SL %" value={formatPct(s.sl_percent)} />
              <Stat
                label="Leverage"
                value={s.suggested_leverage || "—"}
                accent="text-[#d97706]"
              />
            </div>
          </div>
        </Panel>

        {/* HTF Trend */}
        <Panel className="lg:col-span-3" data-testid="signal-htf-trend">
          <PanelHeader
            title="HTF Trend Analysis"
            hint="Multi-timeframe alignment"
            action={<ChartLineUp size={14} className="text-slate-500" />}
          />
          <div className="p-4 flex flex-col gap-3">
            {Object.keys(timeframes).length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">
                No HTF data captured.
              </div>
            ) : (
              Object.entries(timeframes).map(([tf, state]) => (
                <div
                  key={tf}
                  data-testid={`htf-${tf}`}
                  className="flex items-center justify-between"
                >
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                    {tf}
                  </span>
                  <Pill tone={TF_TONE[String(state).toLowerCase()] || "neutral"}>
                    {state}
                  </Pill>
                </div>
              ))
            )}
          </div>
        </Panel>

        {/* Strategy & regime card */}
        <Panel className="lg:col-span-4" data-testid="signal-strategy-card">
          <PanelHeader
            title="Strategy Triggered"
            hint="Detection summary"
            action={<Lightning size={14} className="text-slate-500" />}
          />
          <div className="p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Strategy
              </span>
              <span className="text-sm text-slate-900 font-data">
                {STRAT_LABEL[s.strategy] || s.strategy}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Market Regime
              </span>
              <Pill tone="info">{s.market_regime}</Pill>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Direction
              </span>
              <DirectionPill direction={s.direction} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Grade
              </span>
              <GradePill grade={s.grade} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Status
              </span>
              <StatusPill status={s.status} />
            </div>
          </div>
        </Panel>

        {/* Score Breakdown */}
        <Panel className="lg:col-span-6" data-testid="signal-score-breakdown">
          <PanelHeader
            title="Score Breakdown"
            hint={`Total · ${formatNum(s.score, 1)}`}
            action={<Stack size={14} className="text-slate-500" />}
          />
          <div className="py-2 min-h-[200px]">
            {Object.keys(breakdown).length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-slate-400">
                No score breakdown available.
              </div>
            ) : (
              Object.entries(breakdown).map(([k, v]) => (
                <ScoreBar key={k} label={k} value={Number(v)} max={100} />
              ))
            )}
          </div>
        </Panel>

        {/* Conditions Passed */}
        <Panel className="lg:col-span-3" data-testid="signal-conditions-passed">
          <PanelHeader
            title="Conditions Passed"
            hint={`${reasons.length + strongComponents.length} confirmations`}
            action={<CheckCircle size={14} className="text-[#16a34a]" />}
          />
          <div className="p-3 flex flex-col gap-2 max-h-[320px] overflow-y-auto">
            {reasons.length === 0 && strongComponents.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">
                No explicit confirmations recorded.
              </div>
            ) : (
              <>
                {reasons.map((r, i) => (
                  <div
                    key={`r-${i}`}
                    className="flex items-start gap-2 px-2 py-1.5 rounded-sm bg-[#16a34a]/5 border border-[#16a34a]/15"
                  >
                    <CheckCircle
                      size={12}
                      weight="fill"
                      className="text-[#16a34a] mt-0.5 shrink-0"
                    />
                    <span className="text-xs text-slate-800">{r}</span>
                  </div>
                ))}
                {strongComponents.map((c) => (
                  <div
                    key={`s-${c.name}`}
                    className="flex items-start gap-2 px-2 py-1.5 rounded-sm bg-[#16a34a]/5 border border-[#16a34a]/15"
                  >
                    <CheckCircle
                      size={12}
                      weight="fill"
                      className="text-[#16a34a] mt-0.5 shrink-0"
                    />
                    <span className="text-xs text-slate-800 capitalize">
                      {c.name.replace(/_/g, " ")} · {formatNum(c.value, 0)}/100
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </Panel>

        {/* Conditions Failed / Weak */}
        <Panel className="lg:col-span-3" data-testid="signal-conditions-failed">
          <PanelHeader
            title="Conditions Failed"
            hint={`${weakComponents.length} weak factors`}
            action={<XCircle size={14} className="text-[#dc2626]" />}
          />
          <div className="p-3 flex flex-col gap-2 max-h-[320px] overflow-y-auto">
            {weakComponents.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">
                No weak factors — all confluence thresholds met.
              </div>
            ) : (
              weakComponents.map((c) => (
                <div
                  key={c.name}
                  className="flex items-start gap-2 px-2 py-1.5 rounded-sm bg-[#dc2626]/5 border border-[#dc2626]/15"
                >
                  <XCircle
                    size={12}
                    weight="fill"
                    className="text-[#dc2626] mt-0.5 shrink-0"
                  />
                  <span className="text-xs text-slate-800 capitalize">
                    {c.name.replace(/_/g, " ")} · {formatNum(c.value, 0)}/100
                  </span>
                </div>
              ))
            )}
          </div>
        </Panel>

        {/* Indicators Snapshot */}
        <Panel className="lg:col-span-12" data-testid="signal-indicators">
          <PanelHeader
            title="Indicator Snapshot"
            hint="Values captured at signal time"
            action={<Cpu size={14} className="text-slate-500" />}
          />
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.keys(s.indicators || {}).length === 0 ? (
              <div className="col-span-full text-xs text-slate-400 text-center py-6">
                No indicator data.
              </div>
            ) : (
              Object.entries(s.indicators || {}).map(([k, v]) => (
                <div
                  key={k}
                  className="bg-[#f1f5f9] border border-slate-200/70 rounded-sm px-3 py-2"
                >
                  <div className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-bold">
                    {k.replace(/_/g, " ")}
                  </div>
                  <div className="mt-1 text-sm font-data text-slate-900 truncate">
                    {typeof v === "number" ? formatNum(v, 4) : String(v)}
                  </div>
                </div>
              ))
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};
